/**
 * CreativeRefusal — why an upload the operator made did not become artwork,
 * at
 * `workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}/creativeRefusals/{assetId}`.
 *
 * ── IT EXISTS BECAUSE SILENCE ANSWERS EVERY QUESTION AT ONCE ──────────────
 * `asset.webhook.ts` decides each refusal precisely and, without this
 * document, reports it to its own logs alone. A browser that uploaded the
 * bytes then observes one thing — no asset — and cannot separate "the file is
 * not a picture this reader can measure" from "the file is over the cap" from
 * "the trigger has not run yet", so the surface waiting on it can only bound
 * the wait on a timer and say the weakest true sentence there is. The reason
 * is a fact the server already had; this is where it is kept instead of
 * discarded.
 *
 * ── A MEMBER IS A FACT ABOUT THE BYTES, BECAUSE THE RECORD IS PERMANENT ───
 * `describe.schedule.ts` skips any object already carrying one of these and
 * never hands it back to the effect, which is what stops it downloading the
 * same unrunnable bytes every five minutes for as long as the campaign stays
 * open. That skip is sound only while re-deciding a member would answer the
 * same thing, so every reason here is a property of the file itself.
 *
 * A verdict that expires is therefore not a member and cannot become one.
 * `describeCreativeUpload` also declines an upload whose campaign has left the
 * states in which its pictures are still the operator's to change, and answers
 * `campaignClosed` to its caller — but a campaign comes back (`building` →
 * `failed` → `drafting` is one dead build and one operator composing again),
 * and the bytes were fine throughout. Kept here that verdict would outlive the
 * condition that produced it and skip the object forever, orphaning an
 * operator's own file in Storage for a reason that had since cleared. It needs
 * no record: the sweep selects on campaign status, so a closed campaign's
 * objects are not enumerated at all while it is closed, and are enumerated
 * again — still undecided, and described — on the first tick after it returns.
 *
 * ── A SIBLING COLLECTION, BECAUSE A REFUSAL IS NOT AN ASSET ───────────────
 * `CreativeAsset` is a picture a campaign can RUN: `build.webhook.ts`
 * downloads its `storagePath` and refuses the launch unless the bytes still
 * measure the `width` and `height` the document claims, and
 * `Campaign.proposal.assetId` names one of those documents as the picture the
 * money buys. A refusal has no geometry and no runnable bytes, so carrying it
 * there would mean loosening the two fields that check rests on, on every
 * asset, and admitting a document the operator's own grid renders and their
 * proposal can point at. This collection is the alternative to that: nothing
 * about the asset collection changes, and nothing here can ever be chosen.
 *
 * ── THE KEY IS THE ONE THE UPLOADER ALREADY HOLDS ─────────────────────────
 * The browser mints the assetId, writes the bytes to
 * `.../creative/{tier}/{assetId}`, and watches `creativeAssets/{assetId}` for
 * the document that describes them. A refusal is keyed by that same id, so it
 * arrives at a second path the uploader named itself — one `get` on a known
 * document, which is why `read` is declared and `list` is not: no reader here
 * ever enumerates the collection, and a refusal for one operator's upload is
 * of no use to anyone who was not waiting on it.
 *
 * Nothing is client-writable. The reason is the server's own verdict on bytes
 * it measured, exactly as `CreativeAsset`'s geometry is, and a browser that
 * could write one could tell itself its file was fine.
 *
 * Fields justified:
 *   reason — the whole document; the one thing the operator's surface needs
 *            in order to say something truer than "this has not arrived"
 * Excluded:
 *   storagePath — the doc id is the assetId and its path names the campaign,
 *     so the bytes are addressable from where this document sits; nothing
 *     reads these bytes anyway, which is what being refused means
 *   fileName — the browser holding the wait is the browser holding the file,
 *     and it has never let go of the name
 *   a stamp — no reader orders these, no policy ages them out, and each
 *     upload mints a fresh assetId, so a record can only ever answer the one
 *     upload that named it; a field written for a TTL nobody declared is a
 *     field with no reader
 *   an operator-facing sentence — copy is the surface's, in the operator's
 *     own interface language, and a server-composed string would be a second
 *     one free to disagree with the catalog
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

/**
 * The refusals a client's own upload can reach, in the order
 * `describeCreativeUpload` decides them. They are separated because the
 * operator's next move differs: bring a smaller file, or bring a different
 * one. Neither move is waiting, which is what makes both recordable.
 *
 * The remaining outcomes that same effect can answer are not members. An
 * object under no campaign, a campaign that fails its own schema parse and an
 * object outside the watched prefix are facts about the deployment rather than
 * the file, and none is reachable from a client upload anyway
 * (`storage.rules` requires the campaign to exist at the write; a campaign
 * this deployment cannot parse has already taken the whole console down around
 * the operator). `campaignClosed` is the fourth, and is excluded for the
 * opposite reason: it is about the operator's upload, but it expires when the
 * campaign returns, and a collection nothing ages out is the wrong place to
 * keep a verdict that stops being true.
 */
export const CreativeRefusalReasonSchema = z
	.enum(["oversize", "unreadableBytes"])
	.describe(
		"why these bytes will never be artwork — the file was above the cap, or no image container the server can measure was found in it",
	);
export type CreativeRefusalReason = z.infer<typeof CreativeRefusalReasonSchema>;

export const CreativeRefusalSchema = z.object({
	reason: CreativeRefusalReasonSchema,
});
export type CreativeRefusal = z.infer<typeof CreativeRefusalSchema>;

export const CreativeRefusal = entity<Actor>()({
	schema: CreativeRefusalSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.ads.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}/creativeRefusals/{assetId}",
		}),
	);
